<template>
  <section data-testid="shot-review">
    <h3 class="text-h6 mb-3">{{ $t('damage_control.review.title') }}</h3>
    <warning-banner :shot="shot" :inspection="inspection" :zone-key="zone.key" :can-draw="Boolean(imageUrl)" :disabled="drawing || otherBusy('warning')"
                    @draw="drawing = true" @busy="setBusy('warning', $event)" @update:inspection="updated" />
    <figure v-if="inspection.side === 'arrival' && departurePreviewUrl" class="dc-departure">
      <figcaption class="mb-2">{{ $t('damage_control.capture.departure_photo') }}</figcaption>
      <img :src="departurePreviewUrl" :alt="$t('damage_control.capture.departure_photo')" data-testid="departure-photo" @error="previewError(departurePreviewUrl)" />
    </figure>
    <p class="mb-2">{{ $t('damage_control.capture.current_photo') }}</p>
    <finding-drawer v-if="imageUrl" :key="shot.id" :shot="shot" :inspection="inspection" :zone-key="zone.key"
                    :entries="entries" :active.sync="drawing" :image-url="imageUrl" :disabled="otherBusy('drawer')"
                    @busy="setBusy('drawer', $event)" @update:inspection="updated" @preview-error="previewError">
      <template #overlay>
        <div v-for="cell in cells" :key="'cell-' + cell" class="dc-warning-cell" :style="rectStyle(cellRect(cell))"
             :data-cell="cell" data-testid="warning-cell" aria-hidden="true" />
        <div v-for="finding in findings" :key="finding.id" class="dc-finding-frame"
             :class="finding.classification === 'known' ? 'dc-known' : 'dc-new'"
             :style="rectStyle(finding.bbox)" :data-finding-id="finding.id" data-testid="finding-frame">
          <span>{{ finding.classification === 'known' ? $t('damage_control.review.known_record_label', { id: finding.known_record_id }) : $t('damage_control.review.classification_new') }}</span>
        </div>
      </template>
    </finding-drawer>
    <p v-else role="status" data-testid="preview-unavailable">{{ $t('damage_control.review.no_preview') }}</p>
    <v-btn v-if="editable && !drawing" outlined class="dc-draw-button my-3" data-testid="draw-finding"
           :disabled="!imageUrl || anyBusy" @click="drawing = true">{{ $t('damage_control.review.draw') }}</v-btn>
    <finding-list :shot="shot" :inspection="inspection" :zone-key="zone.key" :entries="entries" :disabled="drawing || otherBusy('list')"
                  @busy="setBusy('list', $event)" @update:inspection="updated" />
    <zone-review-control :inspection="inspection" :zone="zone" :disabled="drawing || otherBusy('zone')"
                         @busy="setBusy('zone', $event)" @update:inspection="updated" />
  </section>
</template>

<script>
import { shotWarning, cellRect } from "@/utils/damageControlWarnings";
import { shouldRefreshDamageControlPreview } from "@/utils/damageControlPreview";
import FindingDrawer from "./FindingDrawer.vue";
import FindingList from "./FindingList.vue";
import WarningBanner from "./WarningBanner.vue";
import ZoneReviewControl from "./ZoneReviewControl.vue";

export default {
  name: "ShotReview",
  components: { FindingDrawer, FindingList, WarningBanner, ZoneReviewControl },
  props: {
    inspection: { type: Object, required: true }, zone: { type: Object, required: true },
    shot: { type: Object, required: true }, entries: { type: Array, default: () => [] },
    previewUrl: { type: String, default: "" }, departurePreviewUrl: { type: String, default: "" },
  },
  data: () => ({ drawing: false, busySources: {}, failedPreviewUrls: [] }),
  computed: {
    imageUrl() { return this.shot.preview_url || this.previewUrl; },
    findings() { return (this.shot.findings || []).filter(finding => finding.status === "confirmed"); },
    warning() { return shotWarning(this.shot); },
    cells() { return this.warning ? this.warning.display_cells : []; },
    editable() {
      return this.inspection.status === "in_progress" && this.shot.current !== false && !this.shot.superseded_by_id &&
        Boolean(this.zone.current_shot && this.zone.current_shot.id === this.shot.id);
    },
    anyBusy() { return Object.values(this.busySources).some(Boolean); },
  },
  watch: {
    "shot.id"() { this.drawing = false; },
    editable(value) { if (!value) this.drawing = false; },
    anyBusy(value) { this.$emit("busy", value); },
    drawing(value) { this.$emit("drawing", value); },
  },
  methods: {
    cellRect,
    rectStyle(rect) {
      if (!rect) return {};
      return { left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%` };
    },
    setBusy(source, value) { this.$set(this.busySources, source, value); },
    otherBusy(source) { return Object.keys(this.busySources).some(key => key !== source && this.busySources[key]); },
    updated(payload) { this.$emit("update:inspection", payload); },
    previewError(url) {
      if (shouldRefreshDamageControlPreview(this.failedPreviewUrls, url)) this.$emit("preview-error", url);
    },
  },
};
</script>

<style scoped>
.dc-departure { margin: 0 0 16px; max-width: 480px; }
.dc-departure img { display: block; width: 100%; height: auto; }
.dc-warning-cell { position: absolute; box-sizing: border-box; border: 2px solid #ed6c02; background: rgba(255, 193, 7, .22); pointer-events: none; }
.dc-finding-frame { position: absolute; box-sizing: border-box; border: 2px solid; pointer-events: none; }
.dc-new { border-color: #c62828; color: #fff; }
.dc-known { border-color: #1565c0; color: #fff; }
.dc-finding-frame span { position: absolute; left: -2px; top: -2px; padding: 1px 4px; font-size: 12px; line-height: 1.3; max-width: 220px; overflow-wrap: anywhere; }
.dc-new span { background: #c62828; }
.dc-known span { background: #1565c0; }
@media (max-width: 599px) { .dc-draw-button { width: 100%; } }
</style>
